<script setup lang="ts">
  import { formatCamelCase, formatDate } from '@/filters';
  import { useCommonStore } from '@/stores/agency/common';

  const commonStore = useCommonStore();
  const commonState = computed(() => commonStore.$state);
  const confirmationDialog = computed(() => commonState.value.confirmationDialog);

  const iconMap: { [key: string]: string } = {
    delete: 'tabler-trash',
    inactive: 'tabler-ban',
    success: 'tabler-circle-check-filled',
    revert: 'tabler-arrow-back-up'
  };

  const iconColorMap: { [key: string]: string } = {
    success: 'success',
    revert: 'warning'
  };

  const formatIcon = (icon: any): string => iconMap[icon];
</script>

<template>
  <!-- 👉 Confirm Dialog -->
  <VDialog
    :max-width="confirmationDialog.isTableVisible ? '980' : '950'"
    width="auto"
    persistent
    :model-value="confirmationDialog.isDialogVisible"
    @update:model-value="commonStore.confirmationDialog.isDialogVisible = false"
  >
    <!-- Dialog close Btn -->
    <DialogCloseBtn v-if="confirmationDialog.isShowCloseBtn" @click.prevent="commonStore.dismissDialog" />

    <VCard class="text-center px-5 py-3">
      <VCardText>
        <VIcon
          class="mt-3 mb-5"
          size="70"
          :color="iconColorMap[confirmationDialog.icon] ?? 'error'"
          :icon="formatIcon(confirmationDialog.icon)"
        />

        <h3 v-if="confirmationDialog.title" class="text-lg font-weight-bold mb-5">
          {{ confirmationDialog.title }}
        </h3>

        <h6 class="text-lg text-dark font-weight-medium">
          {{
            confirmationDialog.isTableVisible
              ? confirmationDialog.isTableTitle
              : confirmationDialog.confirmationQuestion
          }}
        </h6>

        <div>
          <h3 v-if="confirmationDialog.date" class="text-left font-weight-medium mt-5">
            <b>Date:</b>
            {{ formatDate(confirmationDialog.date) }}
          </h3>
          <h3 v-if="confirmationDialog.time" class="text-left font-weight-medium mt-2">
            <b>Time:</b>
            {{ confirmationDialog.time }}
          </h3>
        </div>
      </VCardText>

      <VCardText v-if="confirmationDialog.isTableVisible">
        <VTable class="text-center mt-2">
          <thead>
            <tr>
              <th v-for="item in confirmationDialog.isTableHeader" :key="item">
                {{ formatCamelCase(item) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in confirmationDialog.isTableData" :key="index">
              <td v-for="(data, key) in item" :key="key">
                {{ data }}
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>

      <VCardText class="d-flex align-center justify-center gap-2">
        <VBtn
          :color="confirmationDialog.cancelText === 'Delete' ? 'error' : 'default'"
          variant="tonal"
          @click="commonStore.cancelDialog"
        >
          {{ confirmationDialog.cancelText }}
        </VBtn>

        <VBtn variant="elevated" @click="commonStore.confirmDialog">
          {{ confirmationDialog.okText }}
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
  :deep(.v-table) {
    max-block-size: 400px !important;
  }

  :deep(.v-table > .v-table__wrapper > table > thead > tr > th) {
    text-align: center !important;
  }
</style>
